import React, { useEffect, useState } from 'react';
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { AddRounded, CancelRounded, CheckCircleRounded, FiberNewRounded, MopedRounded, NavigateNextRounded, RunCircleRounded } from '@mui/icons-material';
import { UniformRequest, UniformRequestStatus, UniformRequestType } from '../../types/UniformRequest';
import { Employee } from '../../types/Employee';
import { queryUniformRequestsByIdNumber } from '../../utils/queryUniformRequests';
import { DateTime } from 'luxon';
import { navigate } from '../../store/slices/breadcrumbSlice';
import { ChipIcon, ChipColor } from '../ChipUtils';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { VacationRequestForm } from '../vacation/VacationRequestForm';
import { UnifromRequestForm } from '../dashboard/uniform/UniformRequestForm';
import { createUniformRequest } from '../../utils/createUniformRequest';
import { SecureComponent } from '../../security/SecureComponent';
import { hasPermission } from '../../security/Permission';
import { useAppSelector } from '../../store';

const getIconFromStatus = (status: UniformRequestStatus) => {
  if (status === 'Processing') return <RunCircleRounded />;
  else if (status === 'Available') return <MopedRounded />;
  else if (status === 'Completed') return <CheckCircleRounded />;
  else if (status === 'Cancelled') return <CancelRounded />;
  return <FiberNewRounded />;
};

const getColorFromStatus = (status: UniformRequestStatus) => {
  if (status === 'Processing') return 'warning';
  else if (status === 'Available') return 'secondary';
  else if (status === 'Completed') return 'success';
  else if (status === 'Cancelled') return 'error';
  return 'info';
};

export const EmployeeUniformRequests = (props: { employee: Employee }) => {
  const { employee } = props;
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const authState = useAppSelector(s => s.auth);

  const [uniformRequests, setUniformRequests] = useState<UniformRequest[]>([]);

  const [type, setType] = useState<UniformRequestType>('Complete Uniform');
  const [showUniformRequestForm, setShowUniformRequestForm] = useState(false);
  const [disableSaveButton, setDisableSaveButton] = useState(false);

  const refresh = () => {
    queryUniformRequestsByIdNumber(employee.idNumber).then(requests => {
      setUniformRequests(requests);
    });
  };

  const handleShowUniformRequestForm = () => setShowUniformRequestForm(true);
  const handleCancelUniformRequestForm = () => {
    setShowUniformRequestForm(false);
    setType('Complete Uniform');
  };

  const handleUniformRequestFormOnChanged = (_e: Employee, t: UniformRequestType) => {
    setType(t);
  };

  const handleSaveUniformRequest = () => {
    if (window.confirm('Are you sure you want to save the uniform request?')) {
      setDisableSaveButton(true);
      createUniformRequest(employee, type).then(r => {
        if (r) {
          refresh();
          enqueueSnackbar('Uniform request has been created!', { variant: 'success' });
        }
        setType('Complete Uniform');
        setShowUniformRequestForm(false);
        setDisableSaveButton(false);
      });
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <Stack>
      <SecureComponent permissions={['create.uniformRequests']}>
        <Stack direction='row-reverse' mb={1}>
          <Button startIcon={<AddRounded />} onClick={handleShowUniformRequestForm}>Create new request</Button>
        </Stack>
      </SecureComponent>
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={76} align="right">Count</TableCell>
                <TableCell width={120}>Id Number</TableCell>
                <TableCell>Name</TableCell>
                <TableCell width={120}>Type</TableCell>
                <TableCell width={220}>Date Requested</TableCell>
                <TableCell width={120}>Status</TableCell>
                <TableCell width={40}>Open</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uniformRequests.length === 0 && (
                <TableRow
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 } 
                  }}>
                  <TableCell colSpan={6}><Typography variant="caption">No uniform requests to show...</Typography></TableCell>
                </TableRow>
              )}
              {uniformRequests.map((u, i) => {
                return (
                  <TableRow 
                    hover={hasPermission(authState, [], ['update.uniformRequests'])}
                    key={u.id}
                    sx={{
                      cursor: 'pointer',
                      '&:last-child td, &:last-child th': { border: 0 } 
                    }}
                  >
                    <TableCell align="right">{i + 1}</TableCell>
                    <TableCell>{u.idNumber}</TableCell>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.type}</TableCell>
                    <TableCell>{u.dateRequested ? DateTime.fromISO(u.dateRequested).toLocaleString(DateTime.DATETIME_MED) : ''}</TableCell>
                    <TableCell>
                      <Chip color={getColorFromStatus(u.status)} icon={getIconFromStatus(u.status)} label={u.status} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dashboard/uniformRequests/${u.id}`, {
                            state: {
                              uniformRequest: u,
                            }
                          })
                        }}
                      >
                        <NavigateNextRounded />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <SecureComponent permissions={['create.uniformRequests']}>
        <Dialog open={showUniformRequestForm} onClose={handleCancelUniformRequestForm} fullWidth>
          <DialogTitle>Create Uniform Request</DialogTitle>
          <DialogContent dividers>
            <UnifromRequestForm employee={employee} onChanged={handleUniformRequestFormOnChanged} />
          </DialogContent>
          <DialogActions>
            <Button color="error" onClick={handleCancelUniformRequestForm}>CANCEL</Button>
            <Button color="primary" onClick={handleSaveUniformRequest} disabled={disableSaveButton}>SAVE</Button>
          </DialogActions>
        </Dialog>
      </SecureComponent>
    </Stack>
  );
};
